import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { MapPin, Calculator, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * Driver-facing dialog to change the arrival address of the current ride.
 * Two-step flow:
 *   1. Driver types a new address → presses "Calculer" → preview shown
 *   2. Confirm modal shows old price / new price / delta → "Confirmer"
 *
 * Props:
 *   - open: bool
 *   - onOpenChange(bool)
 *   - bookingId: str
 *   - currentAddress: str
 *   - onSaved(): refresh hook
 */
export default function ModifyDestinationDialog({
  open,
  onOpenChange,
  bookingId,
  currentAddress,
  onSaved,
}) {
  const [newAddress, setNewAddress] = useState("");
  const [preview, setPreview] = useState(null); // { old_total_price, new_total_price, delta, ... }
  const [busy, setBusy] = useState(false);

  // Reset state on open/close
  useEffect(() => {
    if (!open) {
      setNewAddress("");
      setPreview(null);
      setBusy(false);
    }
  }, [open]);

  const runPreview = async () => {
    const addr = (newAddress || "").trim();
    if (!addr) {
      toast.error("Saisissez la nouvelle adresse d'arrivée");
      return;
    }
    if (addr.toLowerCase() === (currentAddress || "").toLowerCase()) {
      toast.error("La nouvelle adresse est identique à l'actuelle");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/driver/bookings/${bookingId}/modify-destination/preview`,
        { arrival_address: addr },
      );
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await api.post(`/driver/bookings/${bookingId}/modify-destination`, {
        arrival_address: preview.new_arrival_address,
      });
      const delta = preview.delta;
      const msg =
        delta > 0
          ? `Destination modifiée — supplément de ${delta.toFixed(2)} €`
          : delta < 0
          ? `Destination modifiée — réduction de ${Math.abs(delta).toFixed(2)} €`
          : "Destination modifiée — prix inchangé";
      toast.success(msg);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const delta = preview?.delta ?? 0;
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaClass =
    delta > 0
      ? "text-red-600 bg-red-50 border-red-200"
      : delta < 0
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : "text-muted-foreground bg-secondary border-border";
  const deltaLabel =
    delta > 0
      ? `+${delta.toFixed(2)} €`
      : delta < 0
      ? `${delta.toFixed(2)} €`
      : "Aucune variation";

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="modify-destination-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Modifier la destination
          </DialogTitle>
          <DialogDescription>
            Le client souhaite aller ailleurs ? Modifiez l'adresse d'arrivée — le
            prix se recalcule automatiquement avant l'encaissement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs">
            <p className="text-muted-foreground">Adresse actuelle</p>
            <p className="mt-0.5 font-medium text-foreground">{currentAddress}</p>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Nouvelle adresse
            </label>
            <AddressAutocomplete
              value={newAddress}
              onChange={(v) => {
                setNewAddress(v);
                // Reset preview if user edits address after computing
                if (preview) setPreview(null);
              }}
              placeholder="Tapez la nouvelle adresse…"
              data-testid="modify-destination-input"
            />
          </div>

          {!preview && (
            <Button
              onClick={runPreview}
              disabled={busy || !newAddress.trim()}
              className="w-full gap-1.5"
              data-testid="modify-destination-preview-btn"
            >
              <Calculator className="h-4 w-4" />
              {busy ? "Calcul en cours…" : "Calculer le nouveau prix"}
            </Button>
          )}

          {preview && (
            <div className="space-y-3" data-testid="modify-destination-preview">
              <div className="rounded-xl border border-border bg-card p-3 space-y-2">
                <div className="flex items-start gap-2 text-xs">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="text-muted-foreground">Ancien trajet</p>
                    <p className="truncate text-foreground">{preview.old_arrival_address}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {preview.old_distance_km?.toFixed(1)} km · {preview.old_total_price?.toFixed(2)} €
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <ArrowRight className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 flex-none text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="text-muted-foreground">Nouveau trajet</p>
                    <p className="truncate font-medium text-foreground">{preview.new_arrival_address}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {preview.new_distance_km?.toFixed(1)} km · {preview.new_total_price?.toFixed(2)} €
                    </p>
                  </div>
                </div>
              </div>

              <div
                className={`rounded-xl border p-4 flex items-center gap-3 ${deltaClass}`}
                data-testid="modify-destination-delta"
              >
                <DeltaIcon className="h-6 w-6 flex-none" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-wider opacity-80">
                    {delta > 0 ? "Supplément" : delta < 0 ? "Réduction" : "Variation"}
                  </p>
                  <p className="font-[Outfit] text-2xl font-medium leading-tight">
                    {deltaLabel}
                  </p>
                  <p className="text-[11px] opacity-80">
                    Nouveau total à encaisser : <strong>{preview.new_total_price?.toFixed(2)} €</strong>
                    {preview.wait_fee_total > 0 && (
                      <> (dont {preview.wait_fee_total.toFixed(2)} € d'attente)</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="modify-destination-cancel"
          >
            Annuler
          </Button>
          {preview && (
            <Button
              onClick={confirm}
              disabled={busy}
              className="bg-primary hover:bg-primary/90"
              data-testid="modify-destination-confirm"
            >
              {busy ? "Modification…" : "Confirmer la modification"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
